import jsPDF from "jspdf";
import "jspdf-autotable";
import { convertEpochToDate } from "./common";
import { getNpiFromName } from "./npiLookup";

/** Decode HTML entities so PDF shows spaces/symbols instead of literal &nbsp; etc. Handles double-encoded (e.g. &amp;nbsp;). */
function decodeHtmlEntities(str) {
  if (typeof str !== "string") return str;
  const entities = {
    "&nbsp;": " ",
    "&amp;": "&",
    "&lt;": "<",
    "&gt;": ">",
    "&quot;": '"',
    "&#39;": "'",
    "&apos;": "'"
  };
  let out = str;
  let prev;
  let passes = 0;
  const maxPasses = 3;
  do {
    prev = out;
    Object.keys(entities).forEach(entity => {
      out = out.split(entity).join(entities[entity]);
    });
    passes++;
  } while (out !== prev && passes < maxPasses);
  return out;
}

/** Strip HTML tags and normalize note text for PDF: decode entities, add line breaks before Question:/Answer:, replace hyphen separators. */
function normalizeNoteTextForPdf(htmlNote) {
  if (!htmlNote || typeof htmlNote !== "string") return "No notes available.";
  let text = htmlNote.replace(/<\/?[^>]+(>|$)/g, "");
  text = decodeHtmlEntities(text);
  text = text.replace(/-{2,}\s*/g, "\n");
  text = text.replace(/([^\n])Question:/g, "$1\nQuestion:");
  text = text.replace(/([^\n])Answer:/g, "$1\nAnswer:");
  return text.replace(/\n{3,}/g, "\n\n").trim() || "No notes available.";
}

/**
 * Build a chart note PDF in the same format as SingleChart's download.
 * @param {Object} patientDetail - { name, dob }
 * @param {Object} chartNote - { referenceId, authorName, authorId, createdEpoch, note, noteType, referenceType }
 * @returns {Promise<ArrayBuffer>}
 */
export function buildChartNotePdf(patientDetail, chartNote) {
  const pdf = new jsPDF("p", "mm", "a4");
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 14;
  let y = 20;

  const authorName = chartNote?.authorName ?? chartNote?.authorId ?? "Unknown";
  const referenceId = chartNote?.referenceId ?? "N/A";
  const createdDate = chartNote?.createdEpoch != null ? convertEpochToDate(chartNote.createdEpoch) : "N/A";
  const noteMessage = chartNote?.note;

  // Title
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(16);
  pdf.text("Patient Chart Note", pageWidth / 2, y, { align: "center" });
  y += 15;

  // Patient Info
  pdf.setFontSize(12);
  pdf.setFont("helvetica", "normal");
  pdf.text(`Patient Name: ${patientDetail?.name || "N/A"}`, margin, y);
  y += 7;
  let dobText = "N/A";
  if (patientDetail?.dob) {
    const dobDate = new Date(patientDetail.dob);
    if (!isNaN(dobDate.getTime())) {
      const mm = String(dobDate.getUTCMonth() + 1).padStart(2, "0");
      const dd = String(dobDate.getUTCDate()).padStart(2, "0");
      const yyyy = dobDate.getUTCFullYear();
      dobText = `${mm}-${dd}-${yyyy}`;
    }
  }
  pdf.text(`DOB: ${dobText}`, margin, y);
  y += 7;
  pdf.text(`Created: ${createdDate}`, margin, y);
  y += 10;

  pdf.setDrawColor(200);
  pdf.line(margin, y, pageWidth - margin, y);
  y += 8;

  const idLabel =
    chartNote?.noteType === "BELUGA_HEALTH"
      ? "Master ID"
      : chartNote?.referenceType === "BOOKING"
        ? "Booking ID"
        : "Kit ID";
  const authorLabel = chartNote?.noteType === "BELUGA_HEALTH" ? "Provider Name" : "Author";

  pdf.autoTable({
    startY: y,
    headStyles: { fillColor: [0, 128, 96], textColor: 255 },
    head: [["Field", "Value"]],
    body: [
      ["NPI", getNpiFromName(authorName, chartNote?.noteType)],
      [idLabel, referenceId],
      [authorLabel, authorName]
    ],
    theme: "grid",
    styles: { fontSize: 10, cellPadding: 4 },
    margin: { left: margin, right: margin }
  });

  y = pdf.lastAutoTable.finalY + 15;

  pdf.setFont("helvetica", "bold");
  pdf.text("Note:", margin, y);
  y += 8;

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(11);
  const noteText = normalizeNoteTextForPdf(noteMessage);
  const splitText = pdf.splitTextToSize(noteText, pageWidth - margin * 2);
  const footerY = pageHeight - 10;
  const minYBeforeNewPage = footerY - 14;

  splitText.forEach(line => {
    if (y > minYBeforeNewPage) {
      pdf.setFontSize(9);
      pdf.setTextColor(150);
      pdf.text(`${new Date().toLocaleString()}`, pageWidth - margin, footerY, { align: "right" });
      pdf.setTextColor(0);
      pdf.setFontSize(11);
      pdf.addPage();
      y = 20;
    }
    pdf.text(line, margin, y);
    y += 6;
  });

  pdf.setFontSize(9);
  pdf.setTextColor(150);
  pdf.text(`${new Date().toLocaleString()}`, pageWidth - margin, footerY, { align: "right" });

  return Promise.resolve(pdf.output("arraybuffer"));
}
